import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidation, render, type AccountInfo, type ApiClient } from '@sl/core';
import { Banner, Button } from '../../ui';
import { testId } from '../testId';
import { MODE_EXPLAINER, MODE_ICON, SHELL_COPY, modeMessage } from './copy';
import { useShell } from './shellContext';
import styles from './AppShell.module.css';

// DESIGN_SYSTEM §12.2's calm one-time banner on a live, view-only account (REQ-100 AC2, REQ-006) — T-210.
//
// §12.2: "View-only is stated calmly, in neutral colours (it's a fact, not an error)", and the first of its
// three statements is "a one-time dismissible banner". The other two already exist elsewhere and are not this
// component's: the disabled Publish control reads "Publishing is off for this account" (the review surface),
// and the tray offers Copy as checklist / Mark as done instead of Publish (D-010).
//
// **Three decisions worth stating, because each is the reason this file is short.**
//
//   1. **It writes no sentence.** The lead is the chip's own catalogue label (`mode.live_view_only`) and the
//      body is the mode explainer's first line, which already carries §12.2's "AdOrchestrator cannot change your
//      ads." word for word. A banner that retyped either could drift from the chip beside it.
//   2. **"One-time" is the ACCOUNT's memory, not this device's** — `UserPreferences.dismissedHints` (API §10 /
//      §12.8), the field the contract has carried for exactly this since 0.1 and which nothing used yet. So
//      the fact is stated once per person rather than once per browser, which is what "one-time" means to a
//      reader who opens the product on a laptop and then on a desk machine. `localStorage` was the other
//      candidate and is the wrong one here for the same reason `useSavedTheme` keeps the two apart: the local
//      store answers "what should this device paint before the first byte arrives", and this is not that.
//   3. **It is never on a demo account.** The gate is the account's own `mode` — read off the wire, written by
//      nothing in this app — so no address and no stored value can raise it on a demo or suppress it on a live
//      account (REQ-100 AC4, SC-7; `app/t210.mode.test.tsx` is the assertion).
//
// The banner is quiet while the preferences read is in flight. A banner that paints and then disappears on
// every load is worse than one that arrives a beat late, and this one is read once per session.

// **Its two test ids are deliberately NOT in `routes.manifest.ts`' `SHELL_TEST_IDS`** — `shell.view-only` and
// `shell.view-only.dismiss`. That list becomes each shell route's `steadyTestIds`, which is "what a healthy
// idle visit MUST show" and is held against a rendered screen by `routes/spend/SpendFrame.test.tsx`; this
// banner is only ever on a live, view-only account, which no build QA crawls today is. The declaration belongs
// to the stub-live project (TEST_PLAN §2.6 / §2.7, T-153) — see the note where the list would have carried it.

/** `dismissedHints`' key for §12.2's banner. An opaque client key: the server stores it and never reads it. */
export const VIEW_ONLY_HINT = 'shell.view-only';

export interface ViewOnlyNoticeProps {
  account: AccountInfo;
  /** The shell's own client, handed down as `Freshness` takes it — nothing here suspends on the context. */
  client: ApiClient;
}

export function ViewOnlyNotice({ account, client }: ViewOnlyNoticeProps) {
  const { queries, messages } = useShell();
  const queryClient = useQueryClient();
  const preferences = queries.preferences();
  const saved = useQuery({ ...preferences, retry: false });
  const [hidden, setHidden] = useState(false);

  const current = saved.data;
  const dismiss = useCallback(() => {
    if (!current) return;
    // It leaves at once and the write follows: the banner states a fact the reader has just read, so waiting
    // on a round trip before it goes would be the slowest possible way to agree with them. If the write is
    // refused the banner comes back, because the alternative is telling them it was remembered when it was not.
    setHidden(true);
    const body = {
      ...current.data,
      dismissedHints: [...current.data.dismissedHints, VIEW_ONLY_HINT],
    };
    queryClient.setQueryData(preferences.queryKey, { ...current, data: body });
    void (async () => {
      try {
        const response = await client.putPreferences(body);
        queryClient.setQueryData(preferences.queryKey, response);
        await queryClient.invalidateQueries(invalidation.putPreferences(account.id));
      } catch {
        queryClient.setQueryData(preferences.queryKey, current);
        setHidden(false);
      }
    })();
  }, [account.id, client, current, preferences.queryKey, queryClient]);

  if (account.mode !== 'live_view_only') return null;
  if (hidden || !current || current.data.dismissedHints.includes(VIEW_ONLY_HINT)) return null;

  return (
    <Banner
      tone="neutral"
      icon={MODE_ICON.live_view_only}
      title={render(modeMessage('live_view_only'), messages)}
      testID={testId('shell', 'view-only')}
      className={styles.band}
      actions={
        <Button
          variant="ghost"
          size="sm"
          testID={testId('shell', 'view-only', 'dismiss')}
          onClick={dismiss}
        >
          {SHELL_COPY.viewOnlyDismiss}
        </Button>
      }
    >
      {MODE_EXPLAINER.live_view_only.lines[0]}
    </Banner>
  );
}
